import { motion } from 'framer-motion';
import { Facebook, Twitter, Instagram, Youtube, Mail, Phone, MapPin, CreditCard, Shield, Truck, ArrowUp } from 'lucide-react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';

// Styled Components
const FooterStyled = styled.footer`
  background: linear-gradient(135deg, #1a1a2e 0%, #0f0f23 100%);
  color: white;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #007AFF, transparent);
  }
`;

const Container = styled.div`
  max-width: 80rem;
  margin: 0 auto;
  padding: 4rem 1rem 2rem;
  
  @media (min-width: 768px) {
    padding: 4rem 2rem 2rem;
  }
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 3rem;
  margin-bottom: 3rem;
`;

const Section = styled.div`
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: white;
  }
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      margin-bottom: 0.75rem;
      
      a {
        color: #9ca3af;
        text-decoration: none;
        transition: color 0.3s ease;
        
        &:hover {
          color: #007AFF;
        }
      }
    }
  }
`;

const ContactSection = styled.div`
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: white;
  }
  
  .contact-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 1rem;
    color: #9ca3af;
    
    .icon {
      width: 1.25rem;
      height: 1.25rem;
      color: #007AFF;
    }
  }
`;

const NewsletterSection = styled.div`
  h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin-bottom: 1rem;
    color: white;
  }
  
  p {
    color: #9ca3af;
    margin-bottom: 1.5rem;
  }
  
  .newsletter-form {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    
    @media (min-width: 640px) {
      flex-direction: row;
    }
    
    input {
      flex: 1;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      border: 1px solid #374151;
      background: rgba(255, 255, 255, 0.1);
      color: white;
      outline: none;
      transition: all 0.3s ease;
      
      &:focus {
        border-color: #007AFF;
        background: rgba(255, 255, 255, 0.15);
      }
      
      &::placeholder {
        color: #9ca3af;
      }
    }
    
    button {
      background: #007AFF;
      color: white;
      font-weight: 600;
      padding: 0.75rem 2rem;
      border-radius: 0.5rem;
      border: none;
      cursor: pointer;
      transition: all 0.3s ease;
      
      &:hover {
        background: #0056CC;
        transform: translateY(-2px);
      }
    }
  }
`;

const TrustBadges = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 2rem;
  margin-bottom: 3rem;
  padding: 2rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 1rem;
  backdrop-filter: blur(0.5rem);
  
  .badge {
    display: flex;
    align-items: center;
    gap: 1rem;
    
    .badge-icon {
      width: 3rem;
      height: 3rem;
      background: rgba(0, 122, 255, 0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      .icon {
        width: 1.5rem;
        height: 1.5rem;
        color: #007AFF;
      }
    }
    
    .badge-content {
      h4 {
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 0.25rem;
        color: white;
      }
      
      p {
        font-size: 0.875rem;
        color: #9ca3af;
        margin: 0;
      }
    }
  }
`;

const BottomSection = styled.div`
  border-top: 1px solid #374151;
  padding-top: 2rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  
  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  
  .social-link {
    width: 2.5rem;
    height: 2.5rem;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    text-decoration: none;
    transition: all 0.3s ease;
    
    &:hover {
      background: #007AFF;
      transform: translateY(-2px);
    }
    
    .icon {
      width: 1.25rem;
      height: 1.25rem;
    }
  }
`;

const Copyright = styled.div`
  color: #9ca3af;
  text-align: center;
  
  @media (min-width: 768px) {
    text-align: left;
  }
  
  p {
    margin: 0;
    font-size: 0.875rem;
  }
`;

const LegalLinks = styled.div`
  display: flex;
  gap: 2rem;
  flex-wrap: wrap;
  justify-content: center;
  
  @media (min-width: 768px) {
    justify-content: flex-end;
  }
  
  a {
    color: #9ca3af;
    text-decoration: none;
    font-size: 0.875rem;
    transition: color 0.3s ease;
    
    &:hover {
      color: #007AFF;
    }
  }
`;

const ScrollToTop = styled.button`
  position: absolute;
  bottom: 2rem;
  right: 2rem;
  width: 3rem;
  height: 3rem;
  background: #007AFF;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 10px 25px rgba(0, 122, 255, 0.3);
  
  &:hover {
    background: #0056CC;
    transform: translateY(-4px);
  }
  
  .icon {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

const Footer = () => {
  const navigate = useNavigate();
  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const footerSections = [
    {
      title: 'À propos',
      links: [
        { name: 'Notre histoire', href: '/about' },
        { name: 'Mission et valeurs', href: '/about' },
        { name: 'Carrières', href: '#careers' },
        { name: 'Presse', href: '#press' },
        { name: 'Durabilité', href: '#sustainability' },
      ]
    },
    {
      title: 'Service client',
      links: [
        { name: 'FAQ', href: '/faq' },
        { name: 'Contact', href: '/contact' },
        { name: 'SAV', href: '/sav' },
        { name: 'Réparations', href: '/reparations' },
        { name: 'Retours et remboursements', href: '#returns' },
        { name: 'Suivi de commande', href: '#tracking' },
      ]
    },
    {
      title: 'Mentions légales',
      links: [
        { name: 'CGV', href: '#cgv' },
        { name: 'CGU', href: '#cgu' },
        { name: 'Politique de confidentialité', href: '/privacy' },
        { name: 'Cookies', href: '#cookies' },
        { name: 'Mentions légales', href: '/legal' },
      ]
    },
    {
      title: 'Rejoignez-nous',
      links: [
        { name: 'Newsletter', href: '#newsletter' },
        { name: 'Programme fidélité', href: '#loyalty' },
        { name: 'Parrainage', href: '#referral' },
        { name: 'Devenez partenaire', href: '#partners' },
        { name: 'Affiliation', href: '#affiliation' },
      ]
    }
  ];

  const socialLinks = [
    { icon: Facebook, href: '#facebook', label: 'Facebook' },
    { icon: Twitter, href: '#twitter', label: 'Twitter' },
    { icon: Instagram, href: '#instagram', label: 'Instagram' },
    { icon: Youtube, href: '#youtube', label: 'YouTube' },
  ];

  const trustBadges = [
    { icon: Shield, text: 'Paiements sécurisés' },
    { icon: Truck, text: 'Livraison garantie' },
    { icon: CreditCard, text: 'CB, Visa, Mastercard' },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.8,
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
  };

  return (
    <FooterStyled>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <MainContent>
            {/* Footer Sections */}
            {footerSections.map((section, index) => (
              <Section key={index}>
                <h3>{section.title}</h3>
                <ul>
                  {section.links.map((link, linkIndex) => (
                    <li key={linkIndex}>
                      {link.href.startsWith('/') ? (
                        <a 
                          href={link.href}
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(link.href);
                          }}
                        >
                          {link.name}
                        </a>
                      ) : (
                        <a href={link.href}>{link.name}</a>
                      )}
                    </li>
                  ))}
                </ul>
              </Section>
            ))}

            {/* Contact Section */}
            <ContactSection>
              <h3>Contact</h3>
              <div className="contact-item">
                <Mail className="icon" />
                <span>contact@iphone-store.fr</span>
              </div>
              <div className="contact-item">
                <Phone className="icon" />
                <span>0800 123 456</span>
              </div>
              <div className="contact-item">
                <MapPin className="icon" />
                <span>Abidjan, Côte d'Ivoire</span>
              </div>
            </ContactSection>

            {/* Newsletter Section */}
            <NewsletterSection>
              <h3>Newsletter</h3>
              <p>
                Recevez les dernières nouveautés et offres exclusives
              </p>
              <form className="newsletter-form" onSubmit={(e) => e.preventDefault()}>
                <input
                  type="email"
                  placeholder="Votre email"
                  required
                />
                <button type="submit">S'inscrire</button>
              </form>
            </NewsletterSection>
          </MainContent>

          {/* Trust Badges */}
          <TrustBadges>
            <div className="badge">
              <div className="badge-icon">
                <Truck className="icon" />
              </div>
              <div className="badge-content">
                <h4>Livraison gratuite</h4>
                <p>À partir de 50€ d'achat</p>
              </div>
            </div>
            <div className="badge">
              <div className="badge-icon">
                <Shield className="icon" />
              </div>
              <div className="badge-content">
                <h4>Garantie 2 ans</h4>
                <p>Sur tous les produits</p>
              </div>
            </div>
            <div className="badge">
              <div className="badge-icon">
                <CreditCard className="icon" />
              </div>
              <div className="badge-content">
                <h4>Paiement sécurisé</h4>
                <p>SSL et 3D Secure</p>
              </div>
            </div>
          </TrustBadges>

          {/* Bottom Section */}
          <BottomSection>
            {/* Social Links */}
            <SocialLinks>
              <a href="#" className="social-link">
                <Facebook className="icon" />
              </a>
              <a href="#" className="social-link">
                <Twitter className="icon" />
              </a>
              <a href="#" className="social-link">
                <Instagram className="icon" />
              </a>
              <a href="#" className="social-link">
                <Youtube className="icon" />
              </a>
            </SocialLinks>

            {/* Copyright */}
            <Copyright>
              <p>
                © 2024 INZA STORE. Tous droits réservés.
              </p>
            </Copyright>

            {/* Legal Links */}
            <LegalLinks>
              <a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }}>Confidentialité</a>
              <a href="/legal" onClick={(e) => { e.preventDefault(); navigate('/legal'); }}>Conditions</a>
              <a href="#cookies">Cookies</a>
            </LegalLinks>
          </BottomSection>

          {/* Scroll to Top Button */}
          <ScrollToTop onClick={scrollToTop}>
            <ArrowUp className="icon" />
          </ScrollToTop>
        </motion.div>
      </Container>
    </FooterStyled>
  );
};

export default Footer;
